/* eslint-disable max-len */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Accordion, Button } from '@arcflight/tf-component-library';
import { DataValue } from '../../utils/updateLocalDataObject';
import TFInput from '../TFInput/TFInput';
import ToolTip from '../NewTooltip/NewTooltip';
import RadioInputButton from '../RadioInputButton/RadioInputButton';
import { Aircraft, BaseAircraft } from '../../models/aircraft';
import { stringToTitleCase } from '../../utils/utils';
import useGetOperatorSetting from '../../utils/useGetOperatorSetting';
import settingsIcon from '../../assets/icon-card-advanced-settings.svg';
import AircraftSettingsCard from './AircraftSettingsCard';

interface AircraftAdvancedSettingsProps {
  aircraft: Aircraft | BaseAircraft;
  updateAircraftData: (changes: { value: DataValue; key: string }[]) => void;
  handleCancelClick: () => void;
  handleSaveClick: () => void;
  isAdmin: boolean;
}

export const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  row-gap: 24px;
  padding: 0 16px 16px 16px;
  @media (max-width: 1790px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1540px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1310px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 1020px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 451px) {
    grid-template-columns: 1fr;
  }
`;

const OneGridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 20px;
  grid-column: span 6;
  @media (max-width: 1720px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    grid-column: span 5;
  }
  @media (max-width: 1540px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-column: span 4;
  }
  @media (max-width: 1310px) {
    grid-template-columns: 1fr 1fr 1fr;
    grid-column: span 3;
  }
  @media (max-width: 1020px) {
    grid-template-columns: 1fr 1fr;
    grid-column: span 2;
  }
`;

export const Label = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-bottom: 4px;
  color: ${({ edit }): string => (edit ? '#242d41' : 'rgba(36, 45, 65, 0.4)')};
  font-weight: ${({ edit }): string => (edit ? '600' : '400')};
  line-height: ${({ edit }): string => (edit ? '1.33' : 'normal')};
  text-transform: ${({ edit }): string => (edit ? 'none' : 'uppercase')};
`;

export const Text = styled.div`
  line-height: 1.43;
  color: rgba(36, 54, 65, 0.7);
  min-height: 18px;
  text-transform: ${({ cap }): string => (cap ? 'capitalize' : 'none')};
  max-width: ${({ maxWidth }): string => maxWidth || 'unset'};
`;

export const ItemWrapper = styled.div`
  grid-column: ${({ span }): string => (span ? `span ${span}` : 'auto')};
  padding-right: 24px;
  @media (max-width: 451px) {
    grid-column: 1;
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  margin: 16px 0 16px 16px;
`;

export const SaveWrapper = styled.div`
  margin-right: 16px;
`;

export const StyledInput = styled.input`
  height: 40px;
  width: 100%;
  border-radius: 2px;
  border: solid 1px rgba(36, 45, 65, 0.05);
  background-color: #f3f7fc;
  padding-left: 8px;
`;

export const AccordionWrapper = styled.div`
  margin-bottom: 24px;
`;

const RadioWrapper = styled.div`
  display: flex;
`;

export const SubHeading = styled.div`
  grid-column: inherit;
  font-size: 10px;
  line-height: 1.6;
  color: rgba(36, 45, 65, 0.8);
  border-top: 1px solid rgba(36, 45, 65, 0.05);
  padding-top: 24px;
`;

const SYNC_LIMIT_TYPES = ['trips', 'defects', 'scheduled_mx_items', 'intermittent_faults', 'cabin_issues']

const AircraftAdvancedSettings: React.FC<AircraftAdvancedSettingsProps> = ({
  aircraft,
  updateAircraftData,
  handleCancelClick,
  handleSaveClick,
  isAdmin,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [formErrors, setFormErrors] = useState(new Map());

  const { formatMessage } = useIntl();
  const approvalReleaseMode = useGetOperatorSetting('approval_release_mode', aircraft?.id);
  const isNotAutoReleaseApprovalMode = Boolean(approvalReleaseMode) && approvalReleaseMode !== 'none';
  const workpacksRequireApproval = aircraft?.app_settings?.workpacks_require_approval;
  const blockWorkpacksRequireApproval = isNotAutoReleaseApprovalMode && !workpacksRequireApproval;

  const handleInputChange = (value: DataValue, key: string): void => {
    const newAppSettings = aircraft?.app_settings || {};
    newAppSettings[`${key}`] = value;
    updateAircraftData([{ value: newAppSettings, key: 'app_settings' }]);
  };

  const handleSyncInputChange = (value: string, key: string): void => {
    const newSyncLimits = aircraft?.app_settings?.sync_limits || {};
    let newValue = value as number | string;
    if (!value) {
      newValue = 0;
    } else {
      newValue = parseInt(value, 10);
    }
    if (newValue < 20) {
      formErrors.set(key, { status: 'error', message: formatMessage({ id: 'text.valueCannotBeLessThan20' }) });
    } else if (newValue < 50) {
      formErrors.set(key, { status: 'warning', message: formatMessage({ id: 'text.valueIsLessThan50' }) });
    } else {
      formErrors.delete(key);
    }
    setFormErrors(formErrors);
    newSyncLimits[`${key}`] = newValue;
    handleInputChange(newSyncLimits, 'sync_limits');
  };

  const handleLocalCancelClick = (): void => {
    setEditMode(false);
    handleCancelClick();
  };

  const handleLocalSaveClick = (): void => {
    handleSaveClick();
    setEditMode(false);
  };

  const expansionChange = (expansion: boolean): void => {
    if (editMode) {
      setExpanded(true);
    } else {
      setExpanded(expansion);
    }
  };

  const buildSyncLimits = () => {
    return SYNC_LIMIT_TYPES.map((type) => {
      const label = stringToTitleCase(type.replaceAll('_', ' '));
      return (
        <TFInput
          id={`SyncLimit-${type}`}
          value={aircraft?.app_settings?.sync_limits?.[type]}
          label={label}
          onChange={(e) => handleSyncInputChange(e.target.value, type)}
          key={`SyncLimit-${type}`}
          readOnly={!editMode}
          status={formErrors.get(type)?.status}
          statusMessage={formErrors.get(type)?.message}
        />
      );
    });
  };

  return (
    <AccordionWrapper>
      <Accordion
        dash
        add
        isExpanded={expanded}
        onExpansionChange={expansionChange}
        headerContent={
          <AircraftSettingsCard
            icon={settingsIcon}
            title="Advanced settings"
            buttonText="Edit advanced settings"
            setEditMode={setEditMode}
            editMode={editMode}
            isAdmin={isAdmin}
          />
        }
      >
        <StyledGrid>
          <ItemWrapper span="2" data-testid="AircraftAdvancedSettings--WorkpackApproval">
            <Label edit={editMode}>
              Workpack requires approval
              <ToolTip text={formatMessage({ id: 'text.workpackApprovalTooltip' })} />
            </Label>
            {!editMode ? (
              <Text>{workpacksRequireApproval ? 'On' : 'Off'}</Text>
            ) : (
              <RadioWrapper>
                <RadioInputButton
                  text="Yes"
                  active={workpacksRequireApproval}
                  marginRight="8px"
                  onClick={(): void => handleInputChange(true, 'workpacks_require_approval')}
                  disabled={blockWorkpacksRequireApproval}
                />
                <RadioInputButton
                  text="No"
                  active={!workpacksRequireApproval}
                  marginRight="8px"
                  onClick={(): void => handleInputChange(false, 'workpacks_require_approval')}
                  disabled={blockWorkpacksRequireApproval}
                />
              </RadioWrapper>
            )}
          </ItemWrapper>
          <ItemWrapper span="2" data-testid="AircraftAdvancedSettings--IgnoreTolerance">
            <Label edit={editMode}>
              Ignore tolerance for repeats
              <ToolTip text="When creating automatic repeats of scheduled maintenance items, an item completed within + or - of its tolerance will repeat at due + interval by default. If enabled, this will have it repeat at performed + interval regardless of tolerance." />
            </Label>
            {!editMode ? (
              <Text>{aircraft?.app_settings?.ignore_tolerance_for_repeats ? 'On' : 'Off'}</Text>
            ) : (
              <RadioWrapper>
                <RadioInputButton
                  text="Yes"
                  active={aircraft?.app_settings?.ignore_tolerance_for_repeats}
                  marginRight="8px"
                  onClick={(): void => handleInputChange(true, 'ignore_tolerance_for_repeats')}
                />
                <RadioInputButton
                  text="No"
                  active={!aircraft?.app_settings?.ignore_tolerance_for_repeats}
                  marginRight="8px"
                  onClick={(): void => handleInputChange(false, 'ignore_tolerance_for_repeats')}
                />
              </RadioWrapper>
            )}
          </ItemWrapper>
          <ItemWrapper span="2" data-testid="AircraftAdvancedSettings--SendEmails">
            <Label edit={editMode}>
              Send reminder emails
              <ToolTip text="Sends email reminders to crew if a planned flight has not had a preflight hit the server 2 hours after the scheduled departure time. Note that this requires an active flight planning integration." />
            </Label>
            {!editMode ? (
              <Text>{aircraft?.app_settings?.send_reminder_emails ? 'On' : 'Off'}</Text>
            ) : (
              <RadioWrapper>
                <RadioInputButton
                  text="Yes"
                  active={aircraft?.app_settings?.send_reminder_emails}
                  marginRight="8px"
                  onClick={(): void => handleInputChange(true, 'send_reminder_emails')}
                />
                <RadioInputButton
                  text="No"
                  active={!aircraft?.app_settings?.send_reminder_emails}
                  marginRight="8px"
                  onClick={(): void => handleInputChange(false, 'send_reminder_emails')}
                />
              </RadioWrapper>
            )}
          </ItemWrapper>
          <OneGridRow>
            <SubHeading>
              These settings specify how many historic records will be transferred to the iPad Application when loading
              the aircraft. All upcoming defects and maintenance items will always be transferred. A lower number will
              improve the performance of loading the aircraft on a poor internet connection.
            </SubHeading>
          </OneGridRow>
          <OneGridRow>
            {buildSyncLimits()}
          </OneGridRow>
        </StyledGrid>
        {editMode ? (
          <ButtonWrapper>
            <SaveWrapper data-testid="AircraftAdvancedSettings--SaveWrapper">
              <Button height="32px" onClick={handleLocalSaveClick} disabled={Array.from(formErrors.values()).some(value => value.status === 'error')}>
                Save
              </Button>
            </SaveWrapper>
            <Button height="32px" primary={false} onClick={handleLocalCancelClick}>
              Cancel
            </Button>
          </ButtonWrapper>
        ) : null}
      </Accordion>
    </AccordionWrapper>
  );
};

export default AircraftAdvancedSettings;
